import { ListRestart } from "lucide-react";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import PerformanceSummaryPage from "./PerformanceSummaryPage";
import CognitiveThinkingPage from "./CognitiveThinkingPage";
import ClassRankingPage from "./ClassRankingPage";
import ClassAnalysis from "./ClassAnalysis";
import axios from "../axios";
import DiagnosticAnalysisPage from "./DiagnosticAnalysisPage";
import { useSelector } from "react-redux";

function StudentPerformance() {
  const storedData = localStorage.getItem("breathForStudent");
  const studentInformation = useSelector(
    (state) => state.user.currentUser?.subject_information,
  );

  const parsedStudent = JSON.parse(storedData);
  const [student, setStudent] = useState(null);
  const [isDateChanged, setIsDateChanged] = useState(false);
  const [academicYear, setAcademicYear] = useState(null);

  const [assessmentTypeId, setAssessmentTypeId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [assessment, setAssessment] = useState([]);
  const [subjectWiseData, setSubjectWiseData] = useState([]);
  const [subjectList, setSubjectList] = useState([]);

  const [dateRange, setDateRange] = useState([0, 0]);

  const getSubjects = () => {
    if (studentInformation) {
      setSubjectList([...studentInformation]);
    }
  };
  useEffect(() => {
    getSubjects();
  }, [studentInformation]);
  useEffect(() => {
    if (academicYear) {
      const minDate = new Date(academicYear.start_date).getTime();
      const maxDate = new Date(academicYear.end_date).getTime();
      setDateRange([minDate, maxDate]);
    }
  }, [academicYear]);

  const startDateProp = new Date(dateRange[0]).toISOString().split("T")[0]; // '2024-11-29'
  const endDateProp = new Date(dateRange[1]).toISOString().split("T")[0]; // '2025-12-30'

  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = format(date, "MMM"); // Apr
    const year = date.getFullYear();
    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  useEffect(() => {
    if (!parsedStudent) {
      console.log("No Student Data in LocalStorage");
      return;
    }

    if (parsedStudent?.academic_year) {
      setAcademicYear({
        start_date:
          typeof parsedStudent.academic_year.start_date === "string"
            ? new Date(parsedStudent.academic_year.start_date)
                .toISOString()
                .split("T")[0]
            : new Date(parsedStudent.academic_year.start_date)
                .toISOString()
                .split("T")[0],

        end_date:
          typeof parsedStudent.academic_year.end_date === "string"
            ? new Date(parsedStudent.academic_year.end_date)
                .toISOString()
                .split("T")[0]
            : new Date(parsedStudent.academic_year.end_date)
                .toISOString()
                .split("T")[0],
      });
    }
    setStudent(parsedStudent);
    setSchoolId(parsedStudent?.school?.id);
  }, []);

  useEffect(() => {
    const getAssessment = async () => {
      try {
        const res = await axios.get(
          `/performance/assessment-events?schoolId=${parsedStudent?.school?.id}`,
        );
        if (res) {
          setAssessment(res?.data?.data);
        }
      } catch (error) {}
    };
    getAssessment();
  }, [schoolId]);

  useEffect(() => {
    if (!startDateProp || !endDateProp || !student?.id) return;

    const fetchSubjectWiseData = async () => {
      try {
        const response = await axios.get(
          `/performance/student/${student?.id}/subjectWisePercentage/get?startDate=${startDateProp}&endDate=${endDateProp}&assessmentEventId=${assessmentTypeId}`,
        );

        setSubjectWiseData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchSubjectWiseData();
  }, [student, startDateProp, endDateProp, assessmentTypeId]);

  const formatDateForInput = (timestamp) => {
    return new Date(timestamp).toISOString().split("T")[0];
  };

  const handleApply = () => {
    setIsDateChanged(false);
    setShowReport(true);
  };

  const handleDateInputChange = (index, value) => {
    setShowReport(false);
    const newDate = value ? new Date(value).getTime() : dateRange[index];
    const updatedDateRange = [...dateRange];
    updatedDateRange[index] = newDate;

    // Check if end date is before start date
    if (updatedDateRange[0] && updatedDateRange[1]) {
      if (updatedDateRange[1] < updatedDateRange[0]) {
        setError("End date cannot be before start date.");
      } else {
        setError("");
      }
    }

    setDateRange(updatedDateRange);
    setIsDateChanged(true);
    setShowReport(false);
  };
  return (
    <div className="h-[calc(100vh-80px)">
      <div className="w-full border rounded-lg shadow bg-gray-100 py-2 mb-2 sticky top-0 left-0 z-50">
        <h1 className="mt-2 text-center text-2xl font-bold text-[#f77f00]">
          My Performance
        </h1>

        {/* Filters Row */}
        <div className="flex flex-1 flex-row items-center justify-center gap-8 flex-wrap">
          {/* Date pickers */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-col items-center justify-center gap-4 md:flex-row">
              <label className="text-[17px] font-semibold">Start</label>
              <input
                type="date"
                className="text-[16px] border px-2 py-2 rounded"
                value={formatDateForInput(dateRange[0])}
                onChange={(e) => handleDateInputChange(0, e.target.value)}
              />

              <label className="text-[17px] font-semibold">End</label>
              <input
                type="date"
                className="text-[16px] border px-2 py-2 rounded"
                value={formatDateForInput(dateRange[1])}
                onChange={(e) => handleDateInputChange(1, e.target.value)}
              />
            </div>
          </div>

          {/* Assessment Select + Reset + Apply */}
          <div className="flex items-center gap-3">
            <select
              className="border px-2 py-2 rounded min-w-[120px]"
              value={assessmentTypeId || ""}
              onChange={(e) => {
                setAssessmentTypeId(e.target.value);
              }}
            >
              <option value="">Assessment Event</option>
              {assessment?.map((assessmentType) => (
                <option key={assessmentType._id} value={assessmentType._id}>
                  {assessmentType.name}
                </option>
              ))}
            </select>

            {/* Reset button (icon replaced with text button) */}
            <ListRestart
              cursor={"pointer"}
              size={20}
              className="m-3"
              onClick={() => {
                if (setAssessmentTypeId) {
                  setAssessmentTypeId("");
                }

                if (academicYear) {
                  const minDate = new Date(academicYear.start_date).getTime();
                  const maxDate = new Date(academicYear.end_date).getTime();

                  setDateRange([minDate, maxDate]);
                }
                setShowReport(false);
                setError("");
              }}
            />
            <button
              className="px-4 py-2 bg-[#f77f00] text-white rounded-lg hover:bg-orange-400 disabled:opacity-50"
              disabled={!isDateChanged || error !== ""}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <p className="mt-2 text-center text-sm text-red-500">{error}</p>
        )}

        {/* Info banner */}
      </div>

      <div className="w-full border rounded-lg shadow bg-gray-100 p-1">
        {!isDateChanged && (
          <div className="">
            <PerformanceSummaryPage
              student={student}
              startDate={startDateProp}
              endDate={endDateProp}
              assessmentTypeId={assessmentTypeId}
              subjectWiseData={subjectWiseData}
              subjectList={subjectList}
            />
            <CognitiveThinkingPage
              student={student}
              startDate={startDateProp}
              endDate={endDateProp}
              assessmentTypeId={assessmentTypeId}
              subjectWiseData={subjectWiseData}
              subjectList={subjectList}
            />
            <ClassRankingPage
              student={student}
              startDate={startDateProp}
              endDate={endDateProp}
              assessmentTypeId={assessmentTypeId}
              subjectWiseData={subjectWiseData}
              subjectList={subjectList}
            />
            <ClassAnalysis
              student={student}
              startDate={startDateProp}
              endDate={endDateProp}
              assessmentTypeId={assessmentTypeId}
              subjectWiseData={subjectWiseData}
              subjectList={subjectList}
            />
            {/* <DiagnosticAnalysisPage student={student} /> */}
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentPerformance;
